import React, { useCallback, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { cropImageToDataUrl } from "../utils/cropImage";
import "./ImageCropperModal.css";
import { t } from "../i18n";

interface Props {
  imageSrc: string;
  onCancel: () => void;
  onConfirm: (croppedDataUrl: string) => void;
  cropShape?: "rect" | "round";
  aspect?: number;
  confirmLabel?: string;
}

// Full-screen modal that wraps react-easy-crop. Hands the cropped image
// back as a PNG data URL via onConfirm. Same pattern as the QR-center-logo
// cropper on DJQrPage — extracted here so the onboarding avatar can share it.
export default function ImageCropperModal({
  imageSrc,
  onCancel,
  onConfirm,
  cropShape = "round",
  aspect = 1,
  confirmLabel = t("imageCropperModal.useThisCrop"),
}: Props) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!areaPixels || busy) return;
    setBusy(true);
    try {
      const cropped = await cropImageToDataUrl(imageSrc, areaPixels);
      onConfirm(cropped);
    } catch (err) {
      console.error("Failed to crop image:", err);
      setBusy(false);
    }
  };

  return (
    <div className="image-cropper-modal" role="dialog" aria-label={t("imageCropperModal.cropImage")}>
      <div className="image-cropper-modal-cropper">
        <Cropper
          image={imageSrc}
          crop={position}
          zoom={zoom}
          aspect={aspect}
          cropShape={cropShape}
          showGrid={false}
          onCropChange={setPosition}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </div>
      <div className="image-cropper-modal-controls">
        <p className="image-cropper-modal-hint">{t("imageCropperModal.pinchToZoomDrag")}</p>
        <input
          type="range"
          className="image-cropper-modal-zoom-slider"
          min={1}
          max={3}
          step={0.05}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          aria-label={t("imageCropperModal.zoom")}
        />
        <div className="image-cropper-modal-buttons">
          <button
            type="button"
            className="image-cropper-modal-cancel-btn"
            onClick={onCancel}
            disabled={busy}
          >
            {t("imageCropperModal.cancel")}
          </button>
          <button
            type="button"
            className="image-cropper-modal-confirm-btn"
            onClick={handleConfirm}
            disabled={busy || !areaPixels}
          >
            {busy ? t("imageCropperModal.cropping") : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
